import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { omit } from 'lodash'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { Modal } from 'components/Modal'
import { ClientTableAdd } from './ClientTableAdd'
import { Pagination } from 'components/Pagination'
import { useTranslation } from 'react-i18next'
import { FilterClientSchema } from 'store/ClientGroup/ClientGroup.validator'
import {
  getUnrelatedClients,
  linkWithClient
} from 'store/ClientGroup/ClientGroup.actions'
import { all } from 'store/ClientGroup/ClientGroup.selector'

import * as S from './styled'

export type ModalAddClientProps = {
  id: string
  onExit?: () => void
}

interface Filter {
  name: string
  cnpj: string
  limit: number
  page: number
}

const INITIAL_FILTER: Filter = {
  name: '',
  cnpj: '',
  limit: 10,
  page: 0
}

export function ModalAddClient({ id, onExit }: ModalAddClientProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [filter, setFilter] = useState(INITIAL_FILTER)
  const [errors, setErrors] = useState({ ...INITIAL_FILTER })

  const [clientIds, setClientIds] = useState([])
  const {
    loading,
    manageClientGroup,
    unrelatedClients: clients
  } = useSelector(all)

  const { name } = manageClientGroup

  useEffect(() => {
    dispatch(getUnrelatedClients(id))
  }, [dispatch, id])

  async function handleFilter(value: string, name: string) {
    setFilter({ ...filter, [name]: value })
  }

  function onAdd() {
    if (clientIds.length) {
      dispatch(linkWithClient({ id, clientIds }))
      onExit()
    }
  }

  async function onFilter() {
    // filtro
    try {
      await FilterClientSchema().validate(filter, {
        abortEarly: false,
        stripUnknown: true
      })
      dispatch(
        getUnrelatedClients(id, {
          ...filter,
          limit: clients.pagination.limit,
          page: clients.pagination.page
        })
      )
      setErrors({ ...INITIAL_FILTER })
    } catch (ex) {
      const _errors = Object.fromEntries(
        ex.inner.map(({ path, message }) => {
          return [path, message.replace(`${message} `, '')]
        })
      )
      setErrors({ ...INITIAL_FILTER, ..._errors })
    }
  }

  function onFilterPaginate(params: any = {}) {
    dispatch(getUnrelatedClients(params.id, omit(params, 'id')))
  }

  function clearFilter() {
    const params = {
      ...INITIAL_FILTER,
      limit: clients.pagination.limit,
      page: clients.pagination.page
    }
    setFilter(params)
    setErrors({ ...INITIAL_FILTER })
    dispatch(getUnrelatedClients(id, params))
  }

  const button = (
    <Button variant="alternative" onClick={onAdd} size="medium">
      <p>{t('common:add')}</p>
    </Button>
  )

  const content = (
    <S.Wrapper>
      <S.GridHeader>
        <S.GridTitle item container xs={12}>
          <S.BoxTitle>
            {t('common:addCustomerToGroup')} {name}
          </S.BoxTitle>
        </S.GridTitle>
      </S.GridHeader>

      <S.Line />

      <S.Title>{t('breadcrumb:customerList')}</S.Title>

      <S.GridFilter container>
        <S.GridInput item md={12} lg={6}>
          <Input
            error={Boolean(errors.cnpj)}
            fullWidth
            label={t('common:socialReason')}
            value={filter.name}
            onInput={value => handleFilter(value, 'name')}
          />
        </S.GridInput>
        <S.GridInput item md={12} lg={6}>
          <Input
            fullWidth
            label={t('common:identifier')}
            value={filter.cnpj}
            onInput={value => handleFilter(value, 'cnpj')}
          />
        </S.GridInput>
        <S.GridButtons item xs={12}>
          <Button variant="default" onClick={clearFilter} size="medium">
            <p>{t('common:clean')}</p>
          </Button>
          <Button variant="primary" onClick={onFilter} size="medium">
            <p>{t('common:search')}</p>
          </Button>
        </S.GridButtons>
      </S.GridFilter>

      <S.GridTable>
        <ClientTableAdd
          clients={clients.items}
          loading={loading}
          setClients={setClientIds}
        />
        {clients.pagination?.totalItems > 0 && (
          <Pagination
            filters={{
              ...filter,
              id,
              limit: clients.pagination.limit,
              page: clients.pagination.page
            }}
            limit={clients.pagination.limit}
            page={clients.pagination.page}
            totalItems={clients.pagination.totalItems}
            setFilter={onFilterPaginate}
          />
        )}
      </S.GridTable>
    </S.Wrapper>
  )
  return (
    <Modal
      button={button}
      content={content}
      isActionsEnabled={true}
      width={70}
      onExit={onExit}
    />
  )
}
